"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Plus,
  Trash2,
  FileText,
  Eye,
  EyeOff,
  Pencil,
  Layers,
  Filter,
  ArrowUp,
  Copy,
  Link2,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listProposalTemplates,
  updateProposalTemplate,
  deleteProposalTemplate,
  duplicateProposalTemplate,
} from "@/lib/api/proposal-templates";
import { getErrorMessage } from "@/lib/api/client";
import type { ProposalTemplateWithParent } from "@/types";

interface ProposalTemplatesTabProps {
  simulatorId: number;
  versionId: number;
  isEditable: boolean;
}

export function ProposalTemplatesTab({
  simulatorId,
  versionId,
  isEditable,
}: ProposalTemplatesTabProps) {
  const queryClient = useQueryClient();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<ProposalTemplateWithParent | null>(null);

  // Templates query - now uses versionId instead of simulatorId
  const { data: templates, isLoading } = useQuery({
    queryKey: ["proposal-templates", versionId, { active_only: false }],
    queryFn: () => listProposalTemplates(versionId, { active_only: false }),
  });

  const invalidateTemplates = () => {
    // Invalidate all proposal-templates queries for this version (regardless of params)
    queryClient.invalidateQueries({
      queryKey: ["proposal-templates", versionId],
    });
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      templateId,
      data,
    }: {
      templateId: number;
      data: { name?: string; description?: string; is_active?: boolean };
    }) => updateProposalTemplate(templateId, data),
    onSuccess: () => {
      invalidateTemplates();
      toast.success("Template mis a jour");
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (templateId: number) => deleteProposalTemplate(templateId),
    onSuccess: () => {
      invalidateTemplates();
      toast.success("Template supprime");
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: (templateId: number) => duplicateProposalTemplate(templateId),
    onSuccess: () => {
      invalidateTemplates();
      toast.success("Template duplique");
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const handleDeleteClick = (template: ProposalTemplateWithParent) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const handleToggleActive = (template: ProposalTemplateWithParent) => {
    updateMutation.mutate({
      templateId: template.id,
      data: { is_active: !template.is_active },
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Templates de proposition</CardTitle>
          <CardDescription>
            Creez et gerez les templates visuels pour vos propositions
            commerciales
          </CardDescription>
        </div>
        {isEditable && (
          <Button asChild>
            <Link
              to="/admin/simulators/$id/templates/new"
              params={{ id: String(simulatorId) }}
              search={{ version: versionId }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Créer un template
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {templates?.items.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Aucun template configure</p>
            <p className="text-sm text-muted-foreground mt-2">
              Creez un template visuel pour vos propositions commerciales
            </p>
            {isEditable && (
              <Button variant="outline" className="mt-4" asChild>
                <Link
                  to="/admin/simulators/$id/templates/new"
                  params={{ id: String(simulatorId) }}
                  search={{ version: versionId }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Créer un template
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {templates?.items.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-4 rounded-lg border"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Layers className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to="/admin/simulators/$id/templates/$templateId"
                        params={{
                          id: String(simulatorId),
                          templateId: String(template.id),
                        }}
                        search={{ version: versionId }}
                        className="font-medium hover:underline hover:text-primary"
                      >
                        {template.name}
                      </Link>
                      <Badge
                        variant={template.is_active ? "success" : "secondary"}
                      >
                        {template.is_active ? "Actif" : "Inactif"}
                      </Badge>
                      {/* Parent template indicator */}
                      {template.parent_name && (
                        <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300 bg-blue-50">
                          <Link2 className="h-3 w-3" />
                          Herite de {template.parent_name}
                        </Badge>
                      )}
                      {/* Children count for parent templates */}
                      {template.children_count > 0 && (
                        <Badge variant="outline" className="gap-1 text-purple-600 border-purple-300 bg-purple-50">
                          <Users className="h-3 w-3" />
                          {template.children_count} enfant{template.children_count > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {template.pages_data && (
                        <Badge variant="outline">
                          {template.pages_data.length} page{template.pages_data.length > 1 ? "s" : ""} propre{template.pages_data.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {template.priority > 0 && (
                        <Badge variant="outline" className="gap-1">
                          <ArrowUp className="h-3 w-3" />
                          Priorite {template.priority}
                        </Badge>
                      )}
                      {template.selection_conditions &&
                        template.selection_conditions.conditions &&
                        template.selection_conditions.conditions.length > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <Filter className="h-3 w-3" />
                          {template.selection_conditions.conditions.length} condition
                          {template.selection_conditions.conditions.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {template.description || "Aucune description"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEditable && (
                    <>
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to="/admin/simulators/$id/templates/$templateId"
                          params={{
                            id: String(simulatorId),
                            templateId: String(template.id),
                          }}
                          search={{ version: versionId }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Modifier
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => duplicateMutation.mutate(template.id)}
                        disabled={duplicateMutation.isPending}
                        title="Dupliquer"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(template)}
                        title={
                          template.is_active
                            ? "Desactiver"
                            : "Activer"
                        }
                      >
                        {template.is_active ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(template)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le template ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irreversible. Le template &quot;
              {templateToDelete?.name}&quot; sera definitivement supprime.
              {templateToDelete && templateToDelete.children_count > 0 && (
                <span className="block mt-2 text-orange-600 font-medium">
                  Attention : ce template a {templateToDelete.children_count} template(s) enfant(s) 
                  qui deviendront independants et perdront leurs pages heritees.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                templateToDelete && deleteMutation.mutate(templateToDelete.id)
              }
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
